import { api } from '../../app/api';

import type {
    AddressForm,
    PlayerIdentifier,
    PlayerIdentifierForm,
    Role,
    UserCurrencyBalance,
    UserCurrencyLog,
    UserDetail,
    UserForm,
    UserListItem,
} from './users.schema';
import {
    PlayerIdentifierListResponseSchema,
    PlayerIdentifierResponseSchema,
    RoleListResponseSchema,
    UserCurrencyBalanceListResponseSchema,
    UserCurrencyLogListResponseSchema,
    UserDetailResponseSchema,
    UserListResponseSchema,
} from './users.schema';

type ListArgs = {
    start?: number;
    limit?: number;
    search?: string;
    sort?: string;
    direction?: 'ASC' | 'DESC';
    roleId?: number;
    // Restrict to roles with `level >= minRoleLevel` (e.g. 2 = team members /
    // staff and up). Server-side join on Role.level via findAllParametrized.
    minRoleLevel?: number;
    active?: boolean;
};

// extraFilters is consumed by User.findAllParametrized server-side as a JSON
// blob. Only emit it when at least one filter is set so the param stays absent
// on the default listing.
function buildExtraFilters(args: ListArgs): string | undefined {
    const filters: Record<string, unknown> = {};
    if (args.roleId) filters.roleId = args.roleId;
    if (args.minRoleLevel) filters.minRoleLevel = args.minRoleLevel;
    if (args.active !== undefined) filters.active = args.active;
    return Object.keys(filters).length ? JSON.stringify(filters) : undefined;
}

const usersApi = api.injectEndpoints({
    endpoints: (builder) => ({
        listUsers: builder.query<{ data: UserListItem[]; count: number }, ListArgs | void>({
            query: (args) => {
                const a = args ?? {};
                const { start = 0, limit = 20, search, sort = 'id', direction = 'DESC' } = a;
                return {
                    url: '/users',
                    method: 'GET',
                    params: {
                        _start: start,
                        _end: start + limit,
                        _sort: sort,
                        _order: direction,
                        q: search || undefined,
                        extraFilters: buildExtraFilters(a),
                    },
                };
            },
            transformResponse: (raw: unknown) => {
                const parsed = UserListResponseSchema.parse(raw);
                return { data: parsed.data, count: parsed.count };
            },
            providesTags: (result) =>
                result
                    ? [
                          ...result.data.map((u) => ({ type: 'User' as const, id: u.id })),
                          { type: 'User' as const, id: 'LIST' },
                      ]
                    : [{ type: 'User' as const, id: 'LIST' }],
        }),

        getUser: builder.query<UserDetail, number>({
            query: (id) => ({
                url: '/users/[id]',
                method: 'GET',
                query: { id },
            }),
            transformResponse: (raw: unknown) => UserDetailResponseSchema.parse(raw).data,
            providesTags: (_r, _e, id) => [{ type: 'User', id }],
        }),

        createUser: builder.mutation<UserDetail, UserForm>({
            query: (values) => ({
                url: '/users',
                method: 'POST',
                body: serializeCreate(values),
            }),
            transformResponse: (raw: unknown) => UserDetailResponseSchema.parse(raw).data,
            invalidatesTags: [{ type: 'User', id: 'LIST' }],
        }),

        updateUser: builder.mutation<UserDetail, { id: number; values: UserForm }>({
            query: ({ id, values }) => ({
                url: '/users/[id]',
                method: 'PUT',
                query: { id },
                body: serializeUpdate(values),
            }),
            transformResponse: (raw: unknown) => UserDetailResponseSchema.parse(raw).data,
            invalidatesTags: (_r, _e, { id }) => [
                { type: 'User', id },
                { type: 'User', id: 'LIST' },
            ],
        }),

        deleteUser: builder.mutation<void, number>({
            query: (id) => ({
                url: '/users/[id]',
                method: 'DELETE',
                query: { id },
            }),
            invalidatesTags: (_r, _e, id) => [
                { type: 'User', id },
                { type: 'User', id: 'LIST' },
            ],
        }),

        // Admin-side address edit (PUT /addresses/:id, manager-gated). userId is
        // carried only so the owning user's detail cache refetches the addresses.
        updateAddress: builder.mutation<
            unknown,
            { id: number; userId: number; values: AddressForm }
        >({
            query: ({ id, values }) => ({
                url: '/addresses/[id]',
                method: 'PUT',
                query: { id },
                body: serializeAddress(values),
            }),
            invalidatesTags: (_r, _e, { userId }) => [{ type: 'User', id: userId }],
        }),

        getUserBalances: builder.query<UserCurrencyBalance[], number>({
            query: (userId) => ({
                url: '/v2/users/[userId]/currency',
                method: 'GET',
                query: { userId },
            }),
            transformResponse: (raw: unknown) =>
                UserCurrencyBalanceListResponseSchema.parse(raw).data,
            providesTags: (_r, _e, userId) => [{ type: 'UserCurrency', id: `USER-${userId}` }],
        }),

        listUserCurrencyLogs: builder.query<
            { data: UserCurrencyLog[]; count: number },
            { userId: number; start?: number; limit?: number }
        >({
            query: ({ userId, start = 0, limit = 10 }) => ({
                url: '/v2/users/[userId]/currency/logs',
                method: 'GET',
                query: { userId },
                params: { _start: start, _end: start + limit },
            }),
            transformResponse: (raw: unknown) => {
                const parsed = UserCurrencyLogListResponseSchema.parse(raw);
                return { data: parsed.data, count: parsed.count };
            },
            providesTags: (_r, _e, { userId }) => [
                { type: 'UserCurrencyLog', id: `USER-${userId}` },
            ],
        }),

        adjustUserCurrency: builder.mutation<
            unknown,
            { userId: number; amount: number; currency?: string; note?: string | null }
        >({
            query: ({ userId, amount, currency = 'points', note }) => ({
                url: '/v2/users/[userId]/currency/adjust',
                method: 'POST',
                query: { userId },
                body: { amount, currency, note: note || null },
            }),
            invalidatesTags: (_r, _e, { userId }) => [
                { type: 'UserCurrency', id: `USER-${userId}` },
                { type: 'UserCurrencyLog', id: `USER-${userId}` },
            ],
        }),

        listPlayerIdentifiers: builder.query<PlayerIdentifier[], number>({
            query: (userId) => ({
                url: '/v2/users/[userId]/player-identifiers',
                method: 'GET',
                query: { userId },
            }),
            transformResponse: (raw: unknown) =>
                PlayerIdentifierListResponseSchema.parse(raw).data,
            providesTags: (_r, _e, userId) => [{ type: 'PlayerIdentifier', id: `USER-${userId}` }],
        }),

        createPlayerIdentifier: builder.mutation<
            PlayerIdentifier,
            { userId: number; values: PlayerIdentifierForm }
        >({
            query: ({ userId, values }) => ({
                url: '/v2/users/[userId]/player-identifiers',
                method: 'POST',
                query: { userId },
                body: serializePlayerIdentifier(values),
            }),
            transformResponse: (raw: unknown) => PlayerIdentifierResponseSchema.parse(raw).data,
            invalidatesTags: (_r, _e, { userId }) => [
                { type: 'PlayerIdentifier', id: `USER-${userId}` },
            ],
        }),

        updatePlayerIdentifier: builder.mutation<
            PlayerIdentifier,
            { id: number; userId: number; values: PlayerIdentifierForm }
        >({
            query: ({ id, userId, values }) => ({
                url: '/v2/users/[userId]/player-identifiers/[id]',
                method: 'PUT',
                query: { userId, id },
                body: serializePlayerIdentifier(values),
            }),
            transformResponse: (raw: unknown) => PlayerIdentifierResponseSchema.parse(raw).data,
            invalidatesTags: (_r, _e, { userId }) => [
                { type: 'PlayerIdentifier', id: `USER-${userId}` },
            ],
        }),

        deletePlayerIdentifier: builder.mutation<void, { id: number; userId: number }>({
            query: ({ id, userId }) => ({
                url: '/v2/users/[userId]/player-identifiers/[id]',
                method: 'DELETE',
                query: { userId, id },
            }),
            invalidatesTags: (_r, _e, { userId }) => [
                { type: 'PlayerIdentifier', id: `USER-${userId}` },
            ],
        }),

        listRoles: builder.query<Role[], void>({
            query: () => ({
                url: '/roles',
                method: 'GET',
                params: { _start: 0, _end: 100, _sort: 'level', _order: 'DESC' },
            }),
            transformResponse: (raw: unknown) => RoleListResponseSchema.parse(raw).data,
            providesTags: [{ type: 'Role', id: 'LIST' }],
        }),
    }),
});

function serializeCreate(values: UserForm) {
    return {
        name: values.name || null,
        email: values.email,
        phone: values.phone || null,
        roleId: values.roleId,
        active: values.active,
        newsletters: values.newsletters,
        password: values.password,
        passwordConfirmation: values.passwordConfirmation || values.password,
    };
}

function serializeUpdate(values: UserForm) {
    const body: Record<string, unknown> = {
        name: values.name || null,
        email: values.email,
        phone: values.phone || null,
        roleId: values.roleId,
        active: values.active,
        newsletters: values.newsletters,
    };
    // Only touch the password when the operator typed a new one — otherwise the
    // existing hash must survive the update untouched.
    if (values.password) {
        body.password = values.password;
        body.passwordConfirmation = values.passwordConfirmation || values.password;
    }
    return body;
}

// The form holds customData as JSON text (already zod-validated); the API
// wants the parsed object or null to clear it.
function serializePlayerIdentifier(values: PlayerIdentifierForm) {
    return {
        game: values.game,
        identifier: values.identifier.trim(),
        customData: values.customData?.trim() ? JSON.parse(values.customData) : null,
    };
}

function serializeAddress(values: AddressForm) {
    return {
        addressName: values.addressName || null,
        name: values.name || null,
        email: values.email,
        phoneNumber: values.phoneNumber,
        address: values.address,
        isCompany: values.isCompany,
        companyName: values.isCompany ? values.companyName || null : null,
        vatNumber: values.isCompany ? values.vatNumber || null : null,
        registryNumber: values.isCompany ? values.registryNumber || null : null,
        other: values.other || null,
    };
}

export const {
    useListUsersQuery,
    useGetUserQuery,
    useCreateUserMutation,
    useUpdateUserMutation,
    useDeleteUserMutation,
    useUpdateAddressMutation,
    useGetUserBalancesQuery,
    useListUserCurrencyLogsQuery,
    useAdjustUserCurrencyMutation,
    useListPlayerIdentifiersQuery,
    useCreatePlayerIdentifierMutation,
    useUpdatePlayerIdentifierMutation,
    useDeletePlayerIdentifierMutation,
    useListRolesQuery,
} = usersApi;
